// packages/console/src/api/liveControl.ts
//
// Live-ingest CONTROL-plane client (the loopback control REST behind /api/live).
// This is the management surface the LIVE console drives to provision a stream:
// create it (which mints the stream key the broadcaster presents as the SRT
// streamid), arm/stop/pause/resume the pipeline, and manage push destinations.
// It is the counterpart to api/live.ts, which is the read-only telemetry SSE the
// console renders once a stream is on air.
//
// Shapes mirror services/live-ingest/internal/httpapi/api.go exactly:
//   POST   /v1/live-streams                       -> {id, workspaceId, name, presetId, streamKey, createdAt}
//   GET    /v1/live-streams                       -> {streams: LiveStreamRec[]}
//   POST   /v1/live-streams/{id}/start|stop|pause|resume -> {status, streamId}
//   POST   /v1/live-streams/{id}/destinations     -> LiveDestinationRec
//   GET    /v1/live-streams/{id}/destinations     -> {destinations: LiveDestinationRec[]}
//   DELETE /v1/live-streams/{id}/destinations/{destId} -> 204

import { LIVE_SRT_INGEST, servicePaths } from "./config";
import { request } from "./http";

const u = servicePaths.live;

// LiveStreamRec is a provisioned stream: its id, the minted stream key, and the
// live preset it encodes with. The stream key is secret-ish (admission control),
// so it is shown to the owning workspace and never logged.
export interface LiveStreamRec {
  id: string;
  workspaceId: string;
  name: string;
  presetId: string;
  streamKey: string;
  createdAt: string;
}

// LivePushProto is the restream transport for a destination. rtmps covers the
// major CDNs (YouTube, Twitch); srt is the low-latency relay path.
export type LivePushProto = "rtmps" | "srt";

// LiveDestinationRec is one configured push target for a stream.
export interface LiveDestinationRec {
  id: string;
  streamId: string;
  name: string;
  proto: LivePushProto;
  url: string;
  createdAt: string;
}

// srtIngestUrl builds the exact URL a broadcaster pushes to: the public SRT
// endpoint with the stream key as the streamid. The key is URL-safe (sk_ +
// base64url), but it is still encoded so the URL is copy-paste safe.
export function srtIngestUrl(streamKey: string): string {
  return `${LIVE_SRT_INGEST}?streamid=${encodeURIComponent(streamKey)}`;
}

export function createStream(name: string, presetId: string): Promise<LiveStreamRec> {
  return request<LiveStreamRec>(`${u}/v1/live-streams`, {
    method: "POST",
    json: { name, presetId },
  });
}

export async function listStreams(signal?: AbortSignal): Promise<LiveStreamRec[]> {
  const res = await request<{ streams: LiveStreamRec[] }>(`${u}/v1/live-streams`, { signal });
  return res.streams ?? [];
}

// setStreamKey persists a caller-chosen stream key (the SRT streamid a broadcaster
// presents). The server validates the charset and rejects a collision (409); the
// returned record carries the new key, and the ingest URL is rebuilt from it.
export function setStreamKey(id: string, streamKey: string): Promise<LiveStreamRec> {
  return request<LiveStreamRec>(`${u}/v1/live-streams/${encodeURIComponent(id)}/key`, {
    method: "POST",
    json: { streamKey },
  });
}

// lifecycle transitions all return {status, streamId}; the caller re-reads state
// off the telemetry stream rather than trusting the ack, so the ack is dropped.
function transition(id: string, action: "start" | "stop" | "pause" | "resume"): Promise<unknown> {
  return request<unknown>(`${u}/v1/live-streams/${encodeURIComponent(id)}/${action}`, { method: "POST" });
}

export const startStream = (id: string) => transition(id, "start");
export const stopStream = (id: string) => transition(id, "stop");
export const pauseStream = (id: string) => transition(id, "pause");
export const resumeStream = (id: string) => transition(id, "resume");

export interface AddDestinationInput {
  name: string;
  proto: LivePushProto;
  url: string;
  // streamKey is the CDN's own stream key, appended to the push URL by the
  // egress side; it is separate from this stream's ingest key.
  streamKey?: string;
}

export function addDestination(streamId: string, input: AddDestinationInput): Promise<LiveDestinationRec> {
  return request<LiveDestinationRec>(`${u}/v1/live-streams/${encodeURIComponent(streamId)}/destinations`, {
    method: "POST",
    json: { name: input.name, proto: input.proto, url: input.url, streamKey: input.streamKey ?? "" },
  });
}

export async function listDestinations(streamId: string, signal?: AbortSignal): Promise<LiveDestinationRec[]> {
  const res = await request<{ destinations: LiveDestinationRec[] }>(
    `${u}/v1/live-streams/${encodeURIComponent(streamId)}/destinations`,
    { signal },
  );
  return res.destinations ?? [];
}

export function deleteDestination(streamId: string, destId: string): Promise<unknown> {
  return request<unknown>(
    `${u}/v1/live-streams/${encodeURIComponent(streamId)}/destinations/${encodeURIComponent(destId)}`,
    { method: "DELETE" },
  );
}
